






































import { Component, Prop, VModel, Watch, Vue } from 'vue-property-decorator';
import { v4 as uuidv4 } from 'uuid';

@Component<PortalMoneyInput>({
  inheritAttrs: false,
  $_veeValidate: {
    value() {
      return (this as unknown as PortalMoneyInput).modelValue;
    },
    name() {
      const self = this as unknown as PortalMoneyInput;
      return self.name || self.nameInternal;
    }
  }
})
export default class PortalMoneyInput extends Vue {
  @VModel({ type: [Number, String] })
  private modelValue!: number | string | null;

  /**
   * If true, modelValue is expected in cents (integer).
   * Default is false (dollars).
   */
  @Prop({ default: false })
  private inCents!: boolean;

  /**
   * When inCents is false, whether to emit number (parseFloat) or string.
   * Default is true (emits number).
   */
  @Prop({ default: true })
  private returnNumber!: boolean;

  @Prop({ default: true })
  private formatOnBlur!: boolean;

  @Prop({ default: false })
  private readonly!: boolean;

  @Prop({ default: false })
  private disabled!: boolean;

  @Prop({ default: false })
  private required!: boolean;

  @Prop({ default: null })
  private name!: string | null;

  @Prop({ default: null })
  private id!: string | null;

  @Prop({ default: '' })
  private cssClasses!: string;

  @Prop({ default: null })
  private hint!: string | null;

  @Prop({ default: '' })
  private label!: string;

  @Prop({ default: '' })
  private placeholder!: string;

  @Prop({ default: '' })
  private validatePattern!: string | Record<string, unknown>;

  @Prop({ default: true })
  private showError!: boolean;

  private nameInternal = '';
  private idInternal = '';
  private normalizedValue = '';
  private displayValue = '';
  private lastEmittedValue: number | string | null = null;

  public created(): void {
    this.nameInternal = this.name || ('portal_money_input_' + uuidv4());
    this.idInternal = this.id || this.nameInternal;
    this.$validator = this.$parent.$validator;
    this.updateDisplayFromModel();
  }

  @Watch('modelValue')
  private onModelValueChanged(newVal: number | string | null): void {
    if (newVal === this.lastEmittedValue) {
      return;
    }
    this.updateDisplayFromModel();
  }

  @Watch('inCents')
  private onInCentsChanged(): void {
    this.updateDisplayFromModel();
  }

  private updateDisplayFromModel(): void {
    if (this.modelValue === null || this.modelValue === undefined || this.modelValue === '') {
      this.normalizedValue = '';
      this.displayValue = '';
      this.lastEmittedValue = null;
      return;
    }

    const num = Number(this.modelValue);
    if (isNaN(num)) {
      this.normalizedValue = '';
      this.displayValue = '';
      this.lastEmittedValue = null;
      return;
    }

    if (this.inCents) {
      const dollars = num / 100;
      this.normalizedValue = this.formatOnBlur ? dollars.toFixed(2) : dollars.toString();
    } else {
      this.normalizedValue = this.formatOnBlur ? num.toFixed(2) : String(this.modelValue);
    }
    this.displayValue = this.formatForDisplay(this.normalizedValue);
  }

  private formatForDisplay(value: string): string {
    if (!value) return '';

    const [integerPart, decimalPart] = value.split('.');
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return value.includes('.') ? `${groupedInteger}.${decimalPart || ''}` : groupedInteger;
  }

  private hasValidGrouping(raw: string): boolean {
    const separatorsOnly = String(raw || '').replace(/[^0-9.,]/g, '');
    return /^\d{1,3}(?:,\d{3})+(?:\.\d*)?$/.test(separatorsOnly);
  }

  private sanitize(raw: string, groupingCommas = this.hasValidGrouping(raw)): string {
    let val = String(raw || '').replace(/[^0-9.,]/g, '');

    if (groupingCommas) {
      val = val.replace(/,/g, '');
    } else {
      val = val.replace(',', '.').replace(/,/g, '');
    }

    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }

    if (val.includes('.')) {
      const [int, dec] = val.split('.');
      if (dec && dec.length > 2) {
        val = `${int}.${dec.slice(0, 2)}`;
      }
    }

    return val;
  }

  private displayPositionToNormalized(display: string, position: number): number {
    return display.slice(0, position).replace(/,/g, '').length;
  }

  private normalizedPositionToDisplay(display: string, position: number): number {
    if (position <= 0) return 0;

    let normalizedPosition = 0;
    for (let i = 0; i < display.length; i += 1) {
      if (display[i] !== ',') {
        normalizedPosition += 1;
      }
      if (normalizedPosition === position) {
        return i + 1;
      }
    }
    return display.length;
  }

  private isValidNormalizedValue(value: string): boolean {
    return value === '' || /^(?:\d+(?:\.\d{0,2})?|\.\d{0,2})$/.test(value);
  }

  private emitNormalizedValue(): void {
    let emitted: number | string | null = null;

    if (this.normalizedValue !== '' && this.normalizedValue !== '.') {
      const parsedFloat = parseFloat(this.normalizedValue);
      if (!isNaN(parsedFloat)) {
        if (this.inCents) {
          emitted = Math.round(parsedFloat * 100);
        } else if (this.returnNumber) {
          emitted = parsedFloat;
        } else {
          emitted = this.normalizedValue;
        }
      }
    }

    this.lastEmittedValue = emitted;
    this.$emit('input', emitted);
  }

  private setNormalizedValue(value: string, target: HTMLInputElement, caretPosition?: number): void {
    this.normalizedValue = value;
    this.displayValue = this.formatForDisplay(value);
    target.value = this.displayValue;
    this.emitNormalizedValue();

    if (caretPosition !== undefined) {
      const displayCaret = this.normalizedPositionToDisplay(this.displayValue, caretPosition);
      this.$nextTick(() => target.setSelectionRange(displayCaret, displayCaret));
    }
  }

  private replaceSelection(target: HTMLInputElement, insertedValue: string): boolean {
    const selectionStart = target.selectionStart ?? target.value.length;
    const selectionEnd = target.selectionEnd ?? selectionStart;
    const normalizedStart = this.displayPositionToNormalized(target.value, selectionStart);
    const normalizedEnd = this.displayPositionToNormalized(target.value, selectionEnd);
    const candidate = this.normalizedValue.slice(0, normalizedStart)
      + insertedValue
      + this.normalizedValue.slice(normalizedEnd);

    if (!this.isValidNormalizedValue(candidate)) return false;

    this.setNormalizedValue(candidate, target, normalizedStart + insertedValue.length);
    return true;
  }

  private onBeforeInput(event: InputEvent): void {
    const target = event.target as HTMLInputElement;

    if (event.inputType === 'insertText' && (event.data === ',' || event.data === '.')) {
      event.preventDefault();
      this.replaceSelection(target, '.');
      return;
    }

    if (event.inputType !== 'deleteContentBackward' && event.inputType !== 'deleteContentForward') {
      return;
    }

    const selectionStart = target.selectionStart ?? 0;
    const selectionEnd = target.selectionEnd ?? selectionStart;
    if (selectionStart !== selectionEnd) return;

    const deletingGroupingComma = event.inputType === 'deleteContentBackward'
      ? target.value[selectionStart - 1] === ','
      : target.value[selectionStart] === ',';
    if (!deletingGroupingComma) return;

    event.preventDefault();
    const normalizedPosition = this.displayPositionToNormalized(target.value, selectionStart);
    const deletedIndex = event.inputType === 'deleteContentBackward'
      ? normalizedPosition - 1
      : normalizedPosition;
    if (deletedIndex < 0 || deletedIndex >= this.normalizedValue.length) return;

    const candidate = this.normalizedValue.slice(0, deletedIndex)
      + this.normalizedValue.slice(deletedIndex + 1);
    this.setNormalizedValue(candidate, target, deletedIndex);
  }

  private onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value;
    const selectionStart = target.selectionStart ?? raw.length;
    const inputEvent = event as InputEvent;
    const groupingCommas = inputEvent.data !== ','
      && (this.displayValue.includes(',') || this.hasValidGrouping(raw));
    const normalizedCaret = this.sanitize(raw.slice(0, selectionStart), groupingCommas).length;
    const sanitized = this.sanitize(raw, groupingCommas);
    this.setNormalizedValue(sanitized, target, Math.min(normalizedCaret, sanitized.length));
  }

  private getPastedValueCandidates(value: string): string[] {
    const pastedValue = value.trim();
    if (!pastedValue || !/^[0-9.,]+$/.test(pastedValue)) return [];

    const candidates = new Set<string>();
    const addCandidate = (candidate: string): void => {
      if (this.isValidNormalizedValue(candidate) && candidate !== '') {
        candidates.add(candidate);
      }
    };

    if (/^\d+$/.test(pastedValue)) addCandidate(pastedValue);
    if (/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(pastedValue)) {
      addCandidate(pastedValue);
    }
    if (/^(?:\d+(?:,\d{0,2})?|,\d{1,2})$/.test(pastedValue)) {
      addCandidate(pastedValue.replace(',', '.'));
    }
    if (/^\d{1,3}(?:,\d{3})+(?:\.\d{0,2})?$/.test(pastedValue)) {
      addCandidate(pastedValue.replace(/,/g, ''));
    }
    if (/^\d{1,3}(?:\.\d{3})+(?:,\d{0,2})?$/.test(pastedValue)) {
      addCandidate(pastedValue.replace(/\./g, '').replace(',', '.'));
    }
    if (pastedValue === '.' || pastedValue === ',') addCandidate('.');

    return Array.from(candidates);
  }

  private onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const target = event.target as HTMLInputElement;
    const pastedValue = event.clipboardData
      ? event.clipboardData.getData('text/plain') || event.clipboardData.getData('text')
      : '';
    const selectionStart = target.selectionStart ?? target.value.length;
    const selectionEnd = target.selectionEnd ?? selectionStart;
    const normalizedStart = this.displayPositionToNormalized(target.value, selectionStart);
    const normalizedEnd = this.displayPositionToNormalized(target.value, selectionEnd);
    const candidates = this.getPastedValueCandidates(pastedValue)
      .map(value => this.normalizedValue.slice(0, normalizedStart)
        + value
        + this.normalizedValue.slice(normalizedEnd))
      .filter(value => this.isValidNormalizedValue(value));
    const uniqueCandidates = Array.from(new Set(candidates));

    if (uniqueCandidates.length !== 1) {
      this.$nts.error('Unable to determine the pasted number format.');
      return;
    }

    const insertedLength = uniqueCandidates[0].length
      - this.normalizedValue.length
      + (normalizedEnd - normalizedStart);
    this.setNormalizedValue(uniqueCandidates[0], target, normalizedStart + insertedLength);
  }

  private onCopy(event: ClipboardEvent): void {
    const target = event.target as HTMLInputElement;
    const selectionStart = target.selectionStart ?? 0;
    const selectionEnd = target.selectionEnd ?? selectionStart;
    if (selectionStart === selectionEnd || !event.clipboardData) return;

    event.preventDefault();
    const copiedValue = target.value.slice(selectionStart, selectionEnd).replace(/,/g, '');
    event.clipboardData.setData('text/plain', copiedValue);
  }

  private onBlur(event: FocusEvent): void {
    if (this.formatOnBlur && this.normalizedValue) {
      if (this.normalizedValue === '.') {
        this.normalizedValue = '';
        this.displayValue = '';
        this.lastEmittedValue = null;
        this.$emit('input', null);
      } else {
        const num = parseFloat(this.normalizedValue);
        if (!isNaN(num)) {
          this.normalizedValue = num.toFixed(2);
          this.displayValue = this.formatForDisplay(this.normalizedValue);
          if (event.target) {
            (event.target as HTMLInputElement).value = this.displayValue;
          }
          if (!this.inCents && !this.returnNumber && this.lastEmittedValue !== this.normalizedValue) {
            this.lastEmittedValue = this.normalizedValue;
            this.$emit('input', this.normalizedValue);
          }
        }
      }
    }
    this.$emit('blur', event);
  }

  private onFocus(event: FocusEvent): void {
    this.$emit('focus', event);
  }

  get hasVeeValidateErrors(): boolean {
    return Boolean((this as unknown as Record<string, unknown>).VeeValidateErrors);
  }

  get isRequired(): boolean {
    if (this.required) return true;
    if (typeof this.validatePattern === 'string') {
      return this.validatePattern?.includes('required');
    } else {
      return !!this.validatePattern?.required;
    }
  }
}
